import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Edit2, Trash2, Check, Loader2, GripVertical } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface ExpenseCategory {
  id: string;
  name: string;
  color?: string;
  description?: string;
  is_system: boolean;
  is_active: boolean;
  order_index: number;
}

interface ManageCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  onCategoriesChange: () => void;
}

const PRESET_COLORS = [
  '#3B82F6', '#2563EB', '#1D4ED8', '#6366F1', '#8B5CF6', '#A855F7', '#7C3AED',
  '#EC4899', '#EF4444', '#DC2626', '#F97316', '#EA580C', '#F59E0B', '#D97706',
  '#B45309', '#CA8A04', '#84CC16', '#10B981', '#14B8A6', '#0D9488', '#06B6D4',
  '#0891B2', '#64748B', '#94A3B8',
];

export default function ManageCategoriesModal({
  isOpen,
  onClose,
  businessId,
  onCategoriesChange,
}: ManageCategoriesModalProps) {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#3B82F6');
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3B82F6');
  const [isSaving, setIsSaving] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen && businessId) {
      fetchCategories();
    }
  }, [isOpen, businessId]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('business_id', businessId)
        .order('order_index');

      if (error) throw error;
      setCategories(data || []);
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Category name is required');
      return;
    }

    if (categories.some(c => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('A category with that name already exists');
      return;
    }

    setIsSaving(true);
    try {
      const maxOrder = categories.reduce((max, c) => Math.max(max, c.order_index), -1);
      const { error } = await supabase.from('expense_categories').insert({
        business_id: businessId,
        name: trimmed,
        color: newColor,
        order_index: maxOrder + 1,
        is_system: false,
        is_active: true,
      });

      if (error) throw error;

      toast.success('Category added');
      setNewName('');
      setNewColor('#3B82F6');
      setIsAdding(false);
      await fetchCategories();
      onCategoriesChange();
    } catch (error: any) {
      console.error('Error adding category:', error);
      toast.error(error.message || 'Failed to add category');
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (cat: ExpenseCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditColor(cat.color || '#3B82F6');
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      toast.error('Category name is required');
      return;
    }

    if (categories.some(c => c.id !== editingId && c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('A category with that name already exists');
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('expense_categories')
        .update({ name: trimmed, color: editColor })
        .eq('id', editingId);

      if (error) throw error;

      toast.success('Category updated');
      setEditingId(null);
      await fetchCategories();
      onCategoriesChange();
    } catch (error: any) {
      console.error('Error updating category:', error);
      toast.error(error.message || 'Failed to update category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (cat: ExpenseCategory) => {
    try {
      const { error } = await supabase
        .from('expense_categories')
        .update({ is_active: !cat.is_active })
        .eq('id', cat.id);

      if (error) throw error;

      toast.success(cat.is_active ? 'Category hidden' : 'Category restored');
      await fetchCategories();
      onCategoriesChange();
    } catch (error: any) {
      console.error('Error toggling category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleDelete = async (cat: ExpenseCategory) => {
    if (!confirm(`Delete "${cat.name}"? Expenses using this category will become uncategorized.`)) return;

    try {
      const { error } = await supabase
        .from('expense_categories')
        .delete()
        .eq('id', cat.id);

      if (error) throw error;

      toast.success('Category deleted');
      await fetchCategories();
      onCategoriesChange();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      toast.error(error.message || 'Failed to delete category');
    }
  };

  if (!isOpen) return null;

  const activeCategories = categories.filter(c => c.is_active);
  const inactiveCategories = categories.filter(c => !c.is_active);

  const colorPicker = (selected: string, onChange: (color: string) => void) => (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {PRESET_COLORS.map(color => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          className={`w-6 h-6 rounded-full border-2 transition-all ${
            selected === color ? 'border-gray-900 scale-110' : 'border-transparent hover:border-gray-300'
          }`}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-xl w-full ${isMobile ? 'max-w-full mx-2' : 'max-w-lg'} max-h-[85vh] flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Manage Categories</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Active categories */}
              <div className="space-y-1">
                {activeCategories.map(cat => (
                  <div key={cat.id}>
                    {editingId === cat.id ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Category name"
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                        {colorPicker(editColor, setEditColor)}
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={handleSaveEdit}
                            disabled={isSaving}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                          >
                            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="px-3 py-1.5 text-gray-600 text-sm hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 group transition-colors">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color || '#94A3B8' }}
                        />
                        <span className="flex-1 text-sm text-gray-900 font-medium truncate">{cat.name}</span>
                        {cat.is_system && (
                          <span className="text-[10px] text-gray-400 uppercase tracking-wider flex-shrink-0">Default</span>
                        )}
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={() => startEdit(cat)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(cat)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Hide category"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                          {!cat.is_system && (
                            <button
                              onClick={() => handleDelete(cat)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add new category */}
              {isAdding ? (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-3 space-y-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="New category name"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleAdd();
                      if (e.key === 'Escape') { setIsAdding(false); setNewName(''); }
                    }}
                  />
                  {colorPicker(newColor, setNewColor)}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleAdd}
                      disabled={isSaving}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      Add
                    </button>
                    <button
                      onClick={() => { setIsAdding(false); setNewName(''); }}
                      className="px-3 py-1.5 text-gray-600 text-sm hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsAdding(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 text-gray-500 hover:text-blue-600 hover:border-blue-400 rounded-xl text-sm font-medium transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Custom Category
                </button>
              )}

              {/* Inactive categories */}
              {inactiveCategories.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Hidden Categories</h3>
                  <div className="space-y-1">
                    {inactiveCategories.map(cat => (
                      <div key={cat.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gray-50 opacity-60">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color || '#94A3B8' }}
                        />
                        <span className="flex-1 text-sm text-gray-500 truncate line-through">{cat.name}</span>
                        <button
                          onClick={() => handleToggleActive(cat)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          Restore
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
